import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const STARTERS = [
  "What's the difference between NAD+ and Myers Cocktail?",
  "Will an IV really help my hangover?",
  "I'm flying tomorrow — what should I get?",
  "How do I choose between mobile and a clinic?",
];

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the IV Therapy Finder concierge. Ask me about treatments, costs, or what fits your goals." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const newHistory = [...messages, { role: "user", content: msg }];
    setMessages(newHistory);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message: msg,
        history: messages.map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...newHistory, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages([...newHistory, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 z-50 w-14 h-14 rounded-full bg-[#064E3B] hover:bg-[#053d2f] text-white shadow-2xl shadow-[#064E3B]/30 flex items-center justify-center transition-transform hover:scale-105"
          data-testid="chat-widget-open"
          aria-label="Open AI chat"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={2} />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#10B981] animate-pulse" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[min(92vw,380px)] h-[min(70vh,560px)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden" data-testid="chat-widget-panel">
          <div className="bg-[#064E3B] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#B4A081]" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-sm font-semibold">IVTF Concierge</div>
                <div className="text-[10px] text-white/70 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> AI assistant · powered by Claude</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="chat-widget-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#064E3B] text-white rounded-br-md" : "bg-white border border-stone-200 text-stone-800 rounded-bl-md"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-3 py-2 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin text-[#064E3B]" />
                  <span className="text-xs text-stone-500">Thinking…</span>
                </div>
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="space-y-1.5 pt-2">
                <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Try asking</div>
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white border border-stone-200 hover:border-[#064E3B]/40 hover:bg-stone-50 text-stone-700 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-stone-200 bg-white flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              className="flex-1"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={!input.trim() || loading} className="bg-[#064E3B] hover:bg-[#053d2f]" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
